export const BANK_DETAILS_REMINDER_CODES = {
    SENT: 'BANK_DETAILS_REMINDER_EMAILS_SENT',
    NO_CLIENTS: 'NO_ELIGIBLE_CLIENTS',
    INVALID: 'INVALID_REQUEST',
    COMPANY_NOT_FOUND: 'COMPANY_NOT_FOUND',
    FORBIDDEN_COMPANY: 'FORBIDDEN_COMPANY',
    INTERNAL: 'INTERNAL_SERVER_ERROR',
};

export const isStandardRemindersResponse = (result) => {
    if (!result || typeof result !== 'object') return false;
    if (Array.isArray(result.successful_requests) || Array.isArray(result.failed_requests)) {
        return false;
    }
    if (result.code === BANK_DETAILS_REMINDER_CODES.SENT
        || result.code === BANK_DETAILS_REMINDER_CODES.NO_CLIENTS) {
        return true;
    }
    if (Array.isArray(result.details) && result.summary?.eligible_clients != null) {
        return true;
    }
    return result.summary?.emails_skipped_email_list != null;
};

export const normalizeEmailList = (emails) => {
    if (emails == null) return [];
    if (Array.isArray(emails)) return emails.map((e) => String(e).trim()).filter(Boolean);
    const value = String(emails).trim();
    return value ? [value] : [];
};

export const formatReminderLocaleDate = (value, locale = 'fr-FR') => {
    if (value == null || value === '') return '—';
    const raw = String(value).trim();
    const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw);
    const date = isoDateOnly ? new Date(`${raw}T12:00:00`) : new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    return new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeZone: 'Europe/Paris',
    }).format(date);
};

const normalizeStandardReminderDetail = (row) => {
    if (!row || typeof row !== 'object') return null;
    return {
        status: row.status || null,
        reason: row.reason || null,
        emailCode: row.email_code || row.emailCode || null,
        emailListGate: row.email_list_gate ?? row.emailListGate ?? null,
        policyCode: row.policy_code || row.policyCode || null,
        partnerId: row.partner_id || row.partnerId || null,
        message: row.message || row.reason || null,
    };
};

/** Réponse Standard structurée (code + summary + details plats). */
export const normalizeStandardRemindersEmailResult = (result) => {
    if (!isStandardRemindersResponse(result)) return null;

    const summary = result.summary || {};
    const filters = result.filters || {};
    const details = Array.isArray(result.details)
        ? result.details.map(normalizeStandardReminderDetail).filter(Boolean)
        : [];

    return {
        format: 'standard',
        success: result.success === true,
        code: result.code || null,
        message: result.message || '',
        timestamp: result.timestamp || null,
        filters: {
            reminderDateStart: filters.reminder_date_start ?? null,
            reminderDateEnd: filters.reminder_date_end ?? null,
            todayParis: filters.today_paris ?? null,
            latestAllowedReminderDateEnd: filters.latest_allowed_reminder_date_end ?? null,
            minimumDaysAfterTripEnd: filters.minimum_days_after_trip_end ?? null,
            companyName: filters.company_name ?? null,
        },
        summary: {
            eligibleClients: summary.eligible_clients ?? 0,
            emailsSent: summary.emails_sent ?? 0,
            emailsSkipped: summary.emails_skipped ?? 0,
            emailsSkippedEmailList: summary.emails_skipped_email_list ?? 0,
            emailsFailed: summary.emails_failed ?? 0,
        },
        details,
        error: result.error || null,
    };
};

/** Réponse legacy (successful_requests / failed_requests). */
export const normalizeRemindersEmailResult = (result) => {
    if (!result || typeof result !== 'object') return null;

    const standard = normalizeStandardRemindersEmailResult(result);
    if (standard) return standard;

    const successful = Array.isArray(result.successful_requests) ? result.successful_requests : [];
    const failed = Array.isArray(result.failed_requests) ? result.failed_requests : [];
    const summary = result.summary || {};
    const filters = result.filters || {};

    const smsSentCount = successful.filter((row) => row.sms_sent === true).length;

    return {
        format: 'legacy',
        success: result.success === true,
        code: result.code || null,
        message: result.message || '',
        timestamp: result.timestamp || null,
        filters: {
            reminderDateStart: filters.reminder_date_start ?? null,
            reminderDateEnd: filters.reminder_date_end ?? null,
            todayParis: filters.today_paris ?? null,
            latestAllowedReminderDateEnd: filters.latest_allowed_reminder_date_end ?? null,
            minimumDaysAfterTripEnd: filters.minimum_days_after_trip_end ?? null,
            companyName: filters.company_name ?? null,
        },
        summary: {
            totalPoliciesMatched: summary.total_policies_matched ?? (successful.length + failed.length),
            successful: summary.successful ?? successful.length,
            failed: summary.failed ?? failed.length,
            smsSent: smsSentCount,
        },
        successful,
        failed,
        details: [],
        error: result.error || null,
    };
};

export const getStandardRemindersAlertVariant = (normalized) => {
    if (!normalized || normalized.format !== 'standard') return 'secondary';
    if (!normalized.success) return 'danger';
    if (normalized.summary.emailsFailed > 0) return 'warning';
    if (
        normalized.summary.emailsSent === 0
        && (normalized.summary.emailsSkipped > 0 || normalized.summary.emailsSkippedEmailList > 0)
    ) {
        return 'warning';
    }
    return 'success';
};

export const compareReminderValues = (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
    return String(a).localeCompare(String(b), 'fr', { sensitivity: 'base', numeric: true });
};

export const filterReminderRows = (rows, search, searchKeys) => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) =>
        searchKeys.some((key) => {
            const value = row[key];
            if (value == null) return false;
            if (Array.isArray(value)) {
                return value.some((item) => String(item).toLowerCase().includes(query));
            }
            return String(value).toLowerCase().includes(query);
        })
    );
};

export const sortReminderRows = (rows, sortKey, sortDirection, accessor) => {
    if (!sortKey) return rows;
    const getValue = accessor || ((row) => row[sortKey]);
    const sorted = [...rows].sort((left, right) => compareReminderValues(getValue(left), getValue(right)));
    return sortDirection === 'desc' ? sorted.reverse() : sorted;
};

export const paginateReminderRows = (rows, page, pageSize) => {
    const safePage = Math.max(1, page);
    const safeSize = Math.max(1, pageSize);
    const start = (safePage - 1) * safeSize;
    return {
        items: rows.slice(start, start + safeSize),
        total: rows.length,
        page: safePage,
        pageSize: safeSize,
        totalPages: Math.max(1, Math.ceil(rows.length / safeSize)),
    };
};
